import p5 from 'p5';
import _ from 'lodash';
import {scaleLinear} from 'd3';
const a2lScale = scaleLinear();

const Utils = {
  // taken from https://gist.github.com/felipesabino/5066336
  RGBtoCMYK : (RGB) => {
    const result = {};

    const r = RGB.r / 255;
    const g = RGB.g / 255;
    const b = RGB.b / 255;

    result.k = Math.min( 1 - r, 1 - g, 1 - b );
    result.c = ( 1 - r - result.k ) / ( 1 - result.k );
    result.m = ( 1 - g - result.k ) / ( 1 - result.k );
    result.y = ( 1 - b - result.k ) / ( 1 - result.k );

    result.c = Math.round( result.c * 100 );
    result.m = Math.round( result.m * 100 );
    result.y = Math.round( result.y * 100 );
    result.k = Math.round( result.k * 100 );

    return result;
  },

  CMYKtoRGB : (CMYK) => {
		const result = {};

		const c = CMYK.c / 100;
		const m = CMYK.m / 100;
		const y = CMYK.y / 100;
		const k = CMYK.k / 100;

		result.r = 1 - Math.min( 1, c * ( 1 - k ) + k );
		result.g = 1 - Math.min( 1, m * ( 1 - k ) + k );
		result.b = 1 - Math.min( 1, y * ( 1 - k ) + k );

		result.r = Math.round( result.r * 255 );
		result.g = Math.round( result.g * 255 );
		result.b = Math.round( result.b * 255 );

		return result;
	},

  // taken from https://beta.observablehq.com/@tonyhschu/solidifying-my-intuition-around-matrix-transformations
  RotateVector: (vector, theta, origin) => {
    let { x, y } = vector

    if (origin) {
      x -= origin.x;
      y -= origin.y;
    }

    // first column of the matrix
    const xa = x * Math.cos(theta)
    const xb = x * Math.sin(theta)

    // second column of the matrix
    const yc = y * -Math.sin(theta)
    const yd = y * Math.cos(theta)

    x = xa + yc;
    y = xb + yd;

    if (origin) {
      x += origin.x;
      y += origin.y;
    }

    // sum the components
    return {x, y};
  },

  petalPath1: (petalSize, leaf) => {
    let pathD = [];

    const blR = [0.25 * petalSize, 0.5 * petalSize]; // bottom length range
    const baR = [0.55 * Math.PI, 0.65 * Math.PI]; // bottom angle range
    const taR = [1 * Math.PI, 1.2 * Math.PI];
    a2lScale.domain(baR).range(blR);

    let p1 = [0, 0];
    let p2 = [
      p5.prototype.randomGaussian(0),
      p5.prototype.randomGaussian(0.9 * petalSize, 0.5),
    ];

    let cp1a = _.random(baR[0], baR[1]); // first cp angle
    let cp1l = a2lScale(cp1a);
    let cp1 = [
      cp1l * Math.cos(cp1a),
      cp1l * Math.sin(cp1a),
    ];

    let cp2a = _.random(taR[0], taR[1]);
    let cp2l = Math.max(0.25 * petalSize, petalSize - cp1[1]);
    let cp2 = [
      cp2l * Math.cos(cp2a) + p2[0],
      cp2l * Math.sin(cp2a) + p2[1],
    ];

    pathD.push(['M', p1]);
    pathD.push(['C', cp1, cp2, p2]);

    // and add the other size
    p1 = p2;
    p2 = [0, 0];

    cp2a = p5.prototype.randomGaussian(cp1a, 0.05); // first cp angle
    cp2l = p5.prototype.randomGaussian(cp1l);
    cp2 = [
      -cp2l * Math.cos(cp2a),
      cp2l * Math.sin(cp2a),
    ];

    cp1a = _.random(taR[0], taR[1]);
    cp1l = Math.max(0.25 * petalSize, petalSize - cp2[1]);
    cp1 = [
      -cp1l * Math.cos(cp1a) + p1[0],
      cp1l * Math.sin(cp1a) + p1[1],
    ];

    pathD.push(['C', cp1, cp2, p2]);
    return pathD;
  },

  leafPath: (petalSize) => {
    let pathD = [];

    const blR = [0.5 * petalSize, 0.75 * petalSize]; // bottom length range
    const baR = [0.55 * Math.PI, 0.65 * Math.PI]; // bottom angle range
    const taR = [1.3 * Math.PI, 1.4 * Math.PI];
    a2lScale.domain(baR).range(blR);

    let p1 = [0, 0];
    let p2 = [
      p5.prototype.randomGaussian(0, 2),
      p5.prototype.randomGaussian(0.9 * petalSize, 1.5),
    ];

    let cp1a = _.random(baR[0], baR[1]); // first cp angle
    let cp1l = a2lScale(cp1a);
    let cp1 = [
      cp1l * Math.cos(cp1a),
      cp1l * Math.sin(cp1a),
    ];

    let cp2a = _.random(taR[0], taR[1]);
    let cp2l = Math.max(0.25 * petalSize, petalSize - cp1[1]);
    let cp2 = [
      cp2l * Math.cos(cp2a) + p2[0],
      cp2l * Math.sin(cp2a) + p2[1],
    ];

    pathD.push(['M', p1]);
    pathD.push(['C', cp1, cp2, p2]);

    // and add the other size
    p1 = p2;
    p2 = [0, 0];

    cp2a = p5.prototype.randomGaussian(cp1a, 0.1); // first cp angle
    cp2l = p5.prototype.randomGaussian(cp1l, 2);
    cp2 = [
      -cp2l * Math.cos(cp2a),
      cp2l * Math.sin(cp2a),
    ];

    cp1a = _.random(taR[0], taR[1]);
    cp1l = Math.max(0.25 * petalSize, petalSize - cp2[1]);
    cp1 = [
      -cp1l * Math.cos(cp1a) + p1[0],
      cp1l * Math.sin(cp1a) + p1[1],
    ];

    pathD.push(['C', cp1, cp2, p2]);
    return pathD;
  },

  petalPath2: (petalSize) => {
    let pathD = [];

    const blR = [petalSize * 0.75, petalSize]; // bottom length range
    const baR = [0.65 * Math.PI, 0.8 * Math.PI]; // bottom angle range
    const taR = [1.4 * Math.PI, 1.6 * Math.PI];
    a2lScale.domain(baR).range(blR);

    let p1 = [0, 0];
    let p2 = [
      p5.prototype.randomGaussian(0, 5),
      p5.prototype.randomGaussian(0.9 * petalSize, 3)
    ];

    let cp1a = _.random(baR[0], baR[1]); // first cp angle
    let cp1l = a2lScale(cp1a);
    let cp1 = [
      cp1l * Math.cos(cp1a),
      cp1l * Math.sin(cp1a),
    ];

    let cp2a = _.random(taR[0], taR[1]);
    let cp2l = Math.max(0.25 * petalSize, petalSize - cp1[1]);
    let cp2 = [
      cp2l * Math.cos(cp2a) + p2[0],
      cp2l * Math.sin(cp2a) + p2[1],
    ];

    pathD.push(['M', p1]);
    pathD.push(['C', cp1, cp2, p2]);

    // and add the other size
    p1 = p2;
    p2 = [0, 0];

    cp2a = p5.prototype.randomGaussian(cp1a, 0.1); // first cp angle
    cp2l = p5.prototype.randomGaussian(cp1l, 3);
    cp2 = [
      -cp2l * Math.cos(cp2a),
      cp2l * Math.sin(cp2a),
    ];

    cp1a = _.random(taR[0], taR[1]);
    cp1l = Math.max(0.25 * petalSize, petalSize - cp2[1]);
    cp1 = [
      -cp1l * Math.cos(cp1a) + p1[0],
      cp1l * Math.sin(cp1a) + p1[1],
    ];

    pathD.push(['M', p1]);
    pathD.push(['C', cp1, cp2, p2]);
    return pathD;
  },

  petalInnerPath: (pathD, scale, bottom) => {
    pathD = _.clone(pathD);

    const move = [pathD[0][1][0], pathD[0][1][1] + bottom]; // x, y + 4
    pathD.push(['M', [move[0] * scale, move[1] * scale]]);
    let curve2 = pathD[2];
    let curve1 = pathD[1];
    curve2 = [curve2[2], curve2[1], curve1[3]]; // reverse, so cp2, cp1, curve1 p2
    curve1 = [curve1[2], curve1[1], move]; // reverse, cp2, cp1, move p1
    _.each([curve2, curve1], curve => {
      const [cp1, cp2, p2] = curve;
      pathD.push([
        'C',
        [cp1[0] * scale, cp1[1] * scale],
        [cp2[0] * scale, cp2[1] * scale],
        [p2[0] * scale, p2[1] * scale],
      ])
    });

    return pathD;
  },

  stemPath: () => {
    const p1 = [
      _.random(-0.75 * petalSize, -0.5 * petalSize),
      p5.prototype.randomGaussian(2 * petalSize, 10)
    ];
    const p2 = [0, 0];
    const cp = [p1[0], _.random(0, 0.25 * p1[1])];
    return `M${p1} Q${cp} ${p2}`;
  }
};

export default Utils;



// WEBPACK FOOTER //
// ./src/components/utils.js